import { v4 as uuidv4 } from 'uuid';
import format from "date-fns/format"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../hooks/reduxToolkit"
import { present } from "../../../lib/util/present"
import { selectPatients } from "../../reduxToolkit/selectors/dependents"
import { PatientSelect, addIdToPatient } from "../formComponents/patientSelect"
import { assert } from "../../../lib/util/assert"
import { addExpense, ExpenseInsertPayload } from "../../reduxToolkit/expensesSlice"
import { createAttachment } from "../../reduxToolkit/attachmentsSlice"
import isEqual from 'lodash/isEqual'
import { IncidentInsertPayload, addIncident, updateIncident } from '../../reduxToolkit/incidentsSlice';
import { IncidentInputGroup, IncidentInputGroupValue } from '../formComponents/incidentInputGroup';
import { useCustomization } from '../../hooks/useCustomizations';
import { hraCardDisplayLabel, organizationProvidesHraCard } from '../../reduxToolkit/selectors/customizations';
import { Tooltip } from '../tooltip';
import Decimal from 'decimal.js';
import { NotNull } from '../../../types/util';
import { ProviderSelect } from '../formComponents/providerSelect'
import { DEFAULT_PAYMENT_PLAN_CADENCE_DAYS, PaymentPlanFields, PaymentPlanFieldsModel, validatePaymentPlanFields } from '../formComponents/paymentPlanFields';
import { ExpenseAmountFields, validateAmountFields } from '../formComponents/expenseAmountFields';
import { DisclosureSection } from '../formComponents/disclosureSection';
import { useMoreOptionsDisclosure } from '../formComponents/useMoreOptionsDisclosure';
import { useClient } from '../../providers/supabase';
import { useBackendApi } from '../../hooks/useBackendApi';
import { submitTextractFeedback } from '@healthshare/shared/commands/account';
import * as Sentry from '@sentry/react';
import { tryParseDecimal } from '../../../lib/util/tryParseDecimal';
import { useConfirm } from '../../providers/confirmDialog';
import { TextractExpenseData } from '../../../lib/util/extractExpenseFromTextract';


import './newExpenseRowForm.scss';
import { wait } from 'async-toolbox';

export type NewExpenseOnInsertedFn = (expense: ExpenseInsertPayload, incident?: IncidentInsertPayload | null) => void

type NewExpenseFormModel = PaymentPlanFieldsModel & {
  date: string
  patient_name: string
  patient_dob: string
  listedAmount?: string
  discountAmount?: string
  paidAmount?: string
  remainingOwedAmount?: string
  provider: string | null
  provider_id?: string | null
  incident_id?: string | null
  paid_with_hra?: boolean
  is_prepayment_agreement?: boolean
}

export type NewExpenseRowFormProps = {
  onInserted?: NewExpenseOnInsertedFn

  prefilledData?: Partial<NewExpenseFormModel>
  disabledFields?: Array<keyof NewExpenseFormModel>
  /**
   * Provide the incident ID where this expense row should go.
   * Cannot be used with `allowNewIncident`
   */
  incidentId?: string
  allowNewIncident?: boolean
  /**
   * Blob key to attach as a receipt after expense creation
   */
  blobKey?: string
  /**
   * Disable the form submit button
   */
  disabled?: boolean
}

type ValidationErrors = {
  [key in keyof NewExpenseFormModel]?: string
}

export function NewExpenseRowForm({
  onInserted,
  incidentId: requiredIncidentId,
  allowNewIncident,
  prefilledData,
  disabledFields,
  blobKey,
  disabled
}: NewExpenseRowFormProps) {
  const dispatch = useAppDispatch()
  const client = useClient()
  const api = useBackendApi()
  const {membershipId, userId} = useAppSelector((s) => s.membership)
  const patients = useAppSelector(selectPatients).map(addIdToPatient)
  const requiredIncident = useAppSelector((s) => present(requiredIncidentId) && s.incidents.incidents.find((i) => i.id === requiredIncidentId))
  const allBlobs = useAppSelector((s) => s.blobs.blobs)

  const hraCustomizations = useCustomization('hra')
  const canChoosePaidWithHra = organizationProvidesHraCard(hraCustomizations)

  const defaultData = useMemo<Partial<NewExpenseFormModel>>(() => {
    return {
      date: format(Date.now(), 'yyyy-MM-dd'),
      // Auto-fill patient info from the required incident if no patient is available
      patient_name: patients[0]?.full_name || (requiredIncident && requiredIncident.patient_name) || undefined,
      patient_dob: patients[0]?.date_of_birth || (requiredIncident && requiredIncident.patient_dob) || undefined,
      incident_id: requiredIncidentId || undefined,
      // New expenses default to shareable so the radio is pre-selected (matching the DB default).
      // Pharmacy drafts start 'unknown' and are completed via the edit form, not this one.
      shareability: 'shareable',
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [row, setRow] = useState<Partial<NewExpenseFormModel>>({
    ...defaultData,
    ...prefilledData
  })

  const formRef = useRef<HTMLFormElement>(null)
  const [wasValidated, setWasValidated] = useState(false)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const clearValidationError = useCallback((field: keyof ValidationErrors) => {
    setValidationErrors((e) => ({...e, [field]: undefined}))
  }, [setValidationErrors])

  // Update the prefilled state if the prefilledData prop changes. This also governs the
  // amount fields: if the member has already interacted with the discount/still-owed
  // disclosures (which commits a value via ExpenseAmountFields' computeAmounts, diverging
  // from `defaultData`) before an async OCR scan delivers prefilledData, the "only if
  // unchanged from default" guard below intentionally lets the member's own edit win over
  // the slower-arriving OCR guess — the same behavior every other prefillable field gets.
  const prevPrefilledData = useRef<Partial<NewExpenseFormModel>>(row)
  useEffect(() => {
    if (!prefilledData) { return }
    if (isEqual(prefilledData, prevPrefilledData.current)) { return }

    setRow((r) => {
      const newRow = {...r}
      for (const [keyStr, value] of Object.entries(prefilledData)) {
        const key = keyStr as keyof typeof prefilledData

        // Change the form value only if the user hasn't set it
        // i.e. the current value is the default value
        if (typeof value != 'undefined' && value !== null && r[key] === defaultData[key]) {
          console.debug('setting', key, 'to', value, 'from', defaultData[key])
          newRow[key] = value as any
        }
      }
      return newRow
    })
    prevPrefilledData.current = prefilledData
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledData])

  const [selectedIncident, _setSelectedIncident] = useState<IncidentInputGroupValue | null | undefined>(null)

  // When an incident is selected, auto-fill patient info from the incident if currently empty
  const setSelectedIncident = useCallback((value: IncidentInputGroupValue | null | undefined) => {
    _setSelectedIncident(value)
    if (value && 'patient_name' in value && value.patient_name && 'patient_dob' in value && value.patient_dob) {
      setRow((r) => {
        if (!r.patient_name && !r.patient_dob) {
          return { ...r, patient_name: value.patient_name, patient_dob: value.patient_dob }
        }
        return r
      })
    }
  }, [])

  const currentIncidentId = selectedIncident?.id || row.incident_id
  const incidentIsMaternity = useAppSelector((s) => s.incidents.incidents.find((i) => i.id === currentIncidentId)?.is_maternity)

  const [showConfirm] = useConfirm()
  // Keep the original prefilled data for comparison
  const originalPrefilledRef = useRef<Partial<NewExpenseFormModel>>(prefilledData || {})

  const doInsert = async (e: React.FormEvent) => {
    e.preventDefault()
    const hasIncident = present(requiredIncidentId) || present(selectedIncident) || present(row.incident_id)
    if (!validateRow(row, hasIncident, formRef, setValidationErrors)) {
      return
    }
    setWasValidated(true)
    

    let incidentId: string | null | undefined = requiredIncidentId || (selectedIncident && selectedIncident.id)
    let addIncidentAction: ReturnType<typeof addIncident> | undefined = undefined
    const now = new Date().toISOString()

    if (selectedIncident && !present(incidentId)) {
      const description = selectedIncident.description
      assert(description)

      incidentId = uuidv4()

      addIncidentAction = dispatch(addIncident({
        ...selectedIncident,
        id: incidentId,
        updated_at: now,
        created_at: now,

        membership_id: membershipId,
        created_by_user_id: userId,
        start_date: row.date,
        patient_dob: row.patient_dob,
        patient_name: row.patient_name,
        description,
      }))
    } else {
      const incident = requiredIncident || selectedIncident
      const incidentDate = incident && 'start_date' in incident && incident.start_date
      if (incidentDate && row.date < incidentDate) {
        // This expense is before the incident, so we need to update the incident's start date
        dispatch(updateIncident({
          id: incident.id,
          updated_at: now,
          start_date: row.date
        }))
      }
    }
    const remainingOwedAmount = tryParseDecimal(row.remainingOwedAmount)
    const listedAmount = tryParseDecimal(row.listedAmount)
    const discountAmount = tryParseDecimal(row.discountAmount)
    const paidAmount = tryParseDecimal(row.paidAmount)
    
    const isFullyPaid = remainingOwedAmount?.eq(new Decimal(0))
    const postDiscountAmount: Decimal | undefined = (listedAmount && discountAmount) ? listedAmount.sub(discountAmount) : undefined

    const expenseId = uuidv4()
    const addExpensePayload: ExpenseInsertPayload = {
      id: expenseId,
      updated_at: now,
      created_at: now,
      membership_id: membershipId,
      created_by_user_id: userId,
      incident_id: incidentId || null,
      date: row.date,
      patient_dob: row.patient_dob,
      patient_name: row.patient_name,
      provider: row.provider,
      provider_id: row.provider_id,
      paid_with_hra: row.paid_with_hra,
      is_prepayment_agreement: row.is_prepayment_agreement,

      is_payment_plan: row.is_payment_plan,
      payment_plan_total: row.is_payment_plan ? row.payment_plan_total : null,
      expected_installment_amount: row.is_payment_plan ? row.expected_installment_amount : null,
      payment_plan_cadence: row.is_payment_plan ? (row.payment_plan_cadence ?? DEFAULT_PAYMENT_PLAN_CADENCE_DAYS) : null,
      is_recurring_medication: row.is_recurring_medication,

      // Defaults to 'shareable' at the DB level when the member doesn't set it.
      shareability: row.shareability,

      listedAmount: listedAmount?.toFixed(2),
      
      // Legacy naming issue: "paidAmount" is the amount after discounts
      paidAmount: postDiscountAmount?.toFixed(2),
      
      is_fully_paid: isFullyPaid,
      payment_history: null
    }
    if (paidAmount) {
      addExpensePayload.payment_history = {
        _version: '2024-12-24',
        payments: [{
          date: row.date,
          amount: paidAmount.toFixed(2)
        }]
      }
    }
    const addExpenseAction = dispatch(addExpense(addExpensePayload))

    // Create attachment if blobKey is provided (receipt attachment).
    // Dispatch through Redux (like every other attach path, e.g. attachToExpense.tsx
    // and receiptLibraryModal.tsx) so the receipt shows up on the claim immediately
    // instead of only after a sync round-trip. The sync engine persists it to Supabase.
    if (blobKey) {
      // Get the blob from Redux to retrieve its original purpose
      const blob = allBlobs.find((b) => b.key === blobKey)
      const purpose = blob?.blob_original_purpose || 'receipt'
      const attachmentNow = new Date().toISOString()

      dispatch(createAttachment({
        id: uuidv4(),
        created_by_user_id: userId,
        blob_key: blobKey,
        table_name: 'expenses',
        record_id: expenseId,
        created_at: attachmentNow,
        updated_at: attachmentNow,
        membership_id: membershipId,
        purpose: purpose,
      }))
    }

    // After saving, prompt for OCR feedback if applicable
    if (blobKey) {
      await handleOcrFeedbackAfterSave({
        blobKey,
        expenseId,
        membershipId,
        userId,
        originalPrefilled: (originalPrefilledRef.current || {}) as Partial<TextractExpenseData>,
        currentRow: row,
        showConfirm,
        api,
      })
    }

    if (onInserted) {
      onInserted(addExpenseAction.payload, addIncidentAction?.payload ?? null)
    }
  }

  async function handleOcrFeedbackAfterSave(params: {
    blobKey: string,
    expenseId: string,
    membershipId?: string | null,
    userId?: string | null,
    originalPrefilled: Partial<TextractExpenseData>,
    currentRow: Partial<NewExpenseFormModel>,
    showConfirm: ReturnType<typeof useConfirm>[0],
    api: ReturnType<typeof useBackendApi>,
  }) {
    const { blobKey, expenseId, membershipId, originalPrefilled, currentRow, showConfirm, api } = params

    try {
      const original = originalPrefilled
      const hasPrefill = Object.keys(original).length > 0

      let userEditedPrefilledField = false
      if (hasPrefill) {
        for (const key of Object.keys(original) as Array<keyof TextractExpenseData>) {
          const originalValue = original[key]
          // map TextractExpenseData field names to NewExpenseFormModel when needed
          const mapKey: keyof NewExpenseFormModel =
            key === 'date' ? 'date' :
            key === 'provider' ? 'provider' :
            key === 'listedAmount' ? 'listedAmount' :
            key === 'discountAmount' ? 'discountAmount' :
            key === 'paidAmount' ? 'paidAmount' : 'date'

          const currentValue = (currentRow as any)[mapKey]
          if (typeof originalValue !== 'undefined' && originalValue !== null) {
            if ((originalValue ?? '') !== (currentValue ?? '')) {
              userEditedPrefilledField = true
              break
            }
          }
        }
      }

      if (hasPrefill && userEditedPrefilledField) {
        const scanningCorrect = await showConfirm({
          title: 'Was the automatic document scanning correct?',
          message: (
            <div>
              <p>We pre-filled this form from your receipt. Were all the fields correct?</p>
            </div>
          ),
          actions: [
            { title: 'Everything looked correct', isCancel: true },
            { title: 'Some fields were not correct', isConfirm: true },
          ]
        })

        if (scanningCorrect) {
          await wait(400);
          const willingToShare = await showConfirm({
            title: 'Help us improve document scanning',
            message: (
              <div>
                <p>Are you willing to securely share this document with the Healthsharetech team to improve our automatic scanning?</p>
                <p className="text-muted mb-0">We will only use it to improve the feature.</p>
              </div>
            ),
            actions: [
              { title: 'No, thanks', isCancel: true },
              { title: 'Yes, you can use my document', isConfirm: true },
            ]
          })

          if (willingToShare && membershipId) {
            try {
              // `created_by_user_id` comes from the JWT server-side, so it is
              // not sent. This was the last `(client.from as any)` write in the
              // app -- see docs/plans/native-app-sdk-seams.md, Stage 2 Step 4.
              await submitTextractFeedback(api, {
                membershipId,
                blobKey,
                expenseId,
                parsedTextractData: original,
              })
            } catch (e) {
              console.error('Failed to save OCR feedback', e)
              Sentry.captureException(new Error('Failed to save OCR feedback'), {
                extra: {
                  error: e,
                  expenseId,
                  blobKey,
                }
              })
            }
          }
        }
      }
    } catch (err) {
      console.error('Error handling OCR feedback flow', err)
      Sentry.captureException(err)
    }
  }

  const selectedPatient = present(row.patient_name) && present(row.patient_dob) &&
      patients.find((p) => p.full_name === row.patient_name && p.date_of_birth === row.patient_dob)

  const { moreOptionsOpen, setMoreOptionsOverride } = useMoreOptionsDisclosure(row)

  return <form
      ref={formRef}
      className={`new-expense-row-form ${wasValidated && 'was-validated'}`}>
    <div className="input-group has-validation">
      <label className='input-group-text'>Date</label>
      <input name="date" placeholder="DateRange" type="date"
        className={`form-control ${validationErrors.date ? 'is-invalid' : ''}`}
        required
        disabled={disabledFields?.includes('date')}
        value={row.date || ''}
        onChange={(e) => { setRow({...row, date: e.target.value }) }}></input>
      {validationErrors.date && <div className="invalid-feedback show">{validationErrors.date}</div> }
    </div>
    <div className='input-group'>
      <label className='input-group-text'>Provider</label>
      <div className="form-control new-expense-row-form__provider-select-form-control">
        <ProviderSelect
          className={validationErrors.provider ? 'is-invalid' : ''}
          disabled={disabledFields?.includes('provider')}
          value={row.provider ? {
            value: row.provider,
            id: row.provider_id || undefined
          } : null}
          onChange={(newValue) => {
            setRow({
              ...row,
              provider: newValue?.value || null,
              provider_id: newValue?.id || null
            })
          }}
        />
      </div>
      {validationErrors.provider && 
        <div className="invalid-feedback show">{validationErrors.provider}</div>
      }
    </div>
    <div className='input-group has-validation'>
      <label className='input-group-text'>Patient</label>
      {patients && <PatientSelect className={`form-select ${validationErrors.patient_name || validationErrors.patient_dob ? 'is-invalid' : ''}`}
        required
        options={patients}
        value={selectedPatient}
        disabled={disabledFields?.includes('patient_name') && disabledFields?.includes('patient_dob') && present(selectedPatient)}
        onChange={({value}) => {
          if (!value) {
            setRow({
              ...row,
              patient_name: undefined,
              patient_dob: undefined,
              incident_id: undefined
            })
          } else if ('id' in value) {
            setRow({
              ...row,
              patient_name: value.full_name,
              patient_dob: value.date_of_birth,
              incident_id: null
            })
          }
        }} />}


      {validationErrors.patient_dob && <div className="invalid-feedback">{validationErrors.patient_dob}</div> }
      {validationErrors.patient_name && <div className="invalid-feedback">{validationErrors.patient_name}</div> }
    </div>
    
    <ExpenseAmountFields
      value={row}
      onChange={(patch) => setRow((r) => ({...r, ...patch}))}
      validationErrors={validationErrors}
      clearValidationError={clearValidationError}
      disabledFields={disabledFields} />

    <DisclosureSection label="More options" open={moreOptionsOpen} onToggle={setMoreOptionsOverride}>
      {canChoosePaidWithHra &&
        <div className='input-group'>
          <div className='new-expense-row-form__form-check-wrapper'>
            <div className='form-check form-check-inline'>
              <input className='form-check-input' type='radio'
                name='paid_with' id='paid_with_hra' value='hra'
                checked={row.paid_with_hra === true}
                onChange={(e) => {
                  if (e.target.checked) {
                    setRow({...row, paid_with_hra: true})
                  }
                }}></input>
              <label className="form-check-label" htmlFor="paid_with_hra">{hraCardDisplayLabel(hraCustomizations)}</label>
            </div>
            <div className='form-check form-check-inline'>
              <input className='form-check-input' type='radio'
                name='paid_with' id='paid_with_personal' value='personal'
                checked={row.paid_with_hra === false}
                onChange={(e) => {
                  if (e.target.checked) {
                    setRow({...row, paid_with_hra: false})
                  }
                }}></input>
              <label className="form-check-label" htmlFor="paid_with_personal">Personal</label>
            </div>
          </div>
        </div>}

      {incidentIsMaternity &&
      <div className="form-check mt-2">
        <input className="form-check-input" type="checkbox" id="is_pre_payment_agreement"
          checked={!!row.is_prepayment_agreement}
          onChange={(e) => {
            setRow({...row, is_prepayment_agreement: e.target.checked})
          }}
          />
        <label className="form-check-label" htmlFor="is_pre_payment_agreement">
            This is a Pre-Payment Agreement
        </label>
      </div>}

      <PaymentPlanFields
        value={row}
        onChange={(patch) => setRow((r) => ({...r, ...patch}))}
        validationErrors={validationErrors} />
    </DisclosureSection>

    <div className="col-12 mt-2">
      <IncidentInputGroup
        allowNewIncident={allowNewIncident}
        requiredIncidentId={requiredIncidentId}
        required
        selectedPatient={selectedPatient}
        value={selectedIncident}
        onChange={setSelectedIncident} />
      {validationErrors.incident_id && (
        <div className="text-danger small mt-1">{validationErrors.incident_id}</div>
      )}
    </div>

    <div className="col-12 d-flex mt-2">
    <button type='submit'
        className={`btn btn-primary`}
        onClick={doInsert}
        disabled={disabled}>
        Add
    </button>
    </div>
  </form>
}

type CompleteExpenseFormModel = NotNull<NewExpenseFormModel, 'date' | 'patient_dob' | 'patient_name' | 'listedAmount'| 'discountAmount' | 'paidAmount' | 'remainingOwedAmount'>

function validateRow(row: Partial<NewExpenseFormModel>, hasIncident: boolean, formRef: React.RefObject<HTMLFormElement>, setValidationErrors: (errors: ValidationErrors) => void): row is CompleteExpenseFormModel {
  const validationErrors: ValidationErrors = {}
  let isInvalid = false
  
  if (!formRef?.current?.reportValidity()) {
    isInvalid = true
  }
  
  if (!row.patient_dob) { validationErrors.patient_dob = 'Please select a patient'; isInvalid = true;    }
  if (!row.patient_name) { validationErrors.patient_name = 'Please select a patient'; isInvalid = true;  }
  if (!row.date) { validationErrors.date = 'Please select a date'; isInvalid = true;  }
  if (!hasIncident) { validationErrors.incident_id = 'Please assign this expense to an incident'; isInvalid = true; }

  const amountErrors = validateAmountFields(row)
  if (Object.keys(amountErrors).length > 0) {
    Object.assign(validationErrors, amountErrors)
    isInvalid = true
  }

  const planErrors = validatePaymentPlanFields(row)
  if (Object.keys(planErrors).length > 0) {
    Object.assign(validationErrors, planErrors)
    isInvalid = true
  }

  setValidationErrors(validationErrors)

  return !isInvalid
}
